import { ButtonHTMLAttributes, CSSProperties } from 'react';
import Icon from './Icon';
import { useTheme } from '../context/ThemeContext';

type Variant = 'primary' | 'secondary' | 'text' | 'danger' | 'outline' | 'ghost';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  label: string;
  variant?: Variant;
  loading?: boolean;
  icon?: string;
  iconRight?: boolean;
  fullWidth?: boolean;
  size?: 'lg';
  style?: CSSProperties;
}

export default function Button({
  label,
  variant = 'primary',
  loading = false,
  icon,
  iconRight = false,
  fullWidth = true,
  size = 'lg',
  style,
  disabled,
  ...rest
}: ButtonProps) {
  const { colors } = useTheme();
  const variants: Record<Variant, { bg: string; text: string; border: string }> = {
    primary: { bg: colors.accent, text: colors.textInverse, border: colors.accent },
    secondary: { bg: colors.surface, text: colors.text, border: colors.borderStrong },
    text: { bg: 'transparent', text: colors.accent, border: 'transparent' },
    danger: { bg: colors.surface, text: colors.text, border: colors.borderStrong },
    outline: { bg: colors.surface, text: colors.text, border: colors.borderStrong },
    ghost: { bg: 'transparent', text: colors.accent, border: 'transparent' },
  };
  const v = variants[variant];
  const isDisabled = disabled || loading;

  const baseStyle: CSSProperties = {
    padding: size === 'lg' ? '16px 20px' : '14px 20px',
    borderRadius: 12,
    border: `1px solid ${v.border}`,
    backgroundColor: v.bg,
    color: v.text,
    width: fullWidth ? '100%' : undefined,
    minHeight: 56,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 17,
    fontWeight: 600,
    letterSpacing: 0,
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition: 'opacity 120ms ease, transform 90ms ease',
    fontFamily: 'inherit',
    ...style,
  };

  return (
    <button disabled={isDisabled} className="sls-press sls-primary-action" style={baseStyle} {...rest}>
      {loading ? (
        <span
          style={{
            width: 18,
            height: 18,
            border: `2.5px solid ${v.text}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'sls-spin 0.7s linear infinite',
          }}
        />
      ) : (
        <>
          {icon && !iconRight && <Icon name={icon} size={15} color={v.text} />}
          <span>{label}</span>
          {icon && iconRight && <Icon name={icon} size={15} color={v.text} />}
        </>
      )}
    </button>
  );
}
